import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import MatchPlayerCoinsEditor from './MatchPlayerCoinsEditor';
import { 
  FaTrophy, FaUsers, FaMoneyBillWave, FaCalendarAlt,
  FaGamepad, FaEdit, FaCheck, FaTimes, FaCrown,
  FaImage, FaDoorOpen, FaKey, FaSpinner, FaSearch,
  FaFilter, FaSort, FaTrash, FaPlus, FaDownload
} from 'react-icons/fa';
import './AdminMatchPlayersPage.css';

const AdminMatchPlayers = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'match_datetime', direction: 'desc' });
  const [roomDetails, setRoomDetails] = useState({});
  const [selectedWinner, setSelectedWinner] = useState({});
  const [resultImage, setResultImage] = useState({});
  const [expandedMatch, setExpandedMatch] = useState(null);

  // Filter and sort matches
  const filteredMatches = matches.filter(match => {
    // Status filter
    if (activeTab !== 'all' && match.match_status !== activeTab) return false;
    
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    return (
      match.game_name?.toLowerCase().includes(searchLower) ||
      match.match_tag?.toLowerCase().includes(searchLower) ||
      match.players.some(p => 
        p.name.toLowerCase().includes(searchLower) || 
        p.email.toLowerCase().includes(searchLower)
      )
    );
  }).sort((a, b) => {
    if (sortConfig.key === 'players') {
      return sortConfig.direction === 'asc' ? 
        a.players.length - b.players.length : 
        b.players.length - a.players.length;
    }
    if (sortConfig.key === 'prize_pool') {
      return sortConfig.direction === 'asc' ? 
        a.prize_pool - b.prize_pool : 
        b.prize_pool - a.prize_pool;
    }
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const formatDate = (dateString) => {
    const options = { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('https://backend.aubattle.com/api/admin/matches-with-players', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter completed matches from last 24 hours only
      const recentMatches = res.data.filter(match => {
        if (match.match_status !== 'completed') return true;
        const matchTime = new Date(match.match_datetime).getTime();
        return (Date.now() - matchTime) <= 86400000; // 24 hours
      });
      
      setMatches(recentMatches);
    } catch (error) {
      toast.error('Failed to load matches');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitRoomDetails = async (matchId) => {
    if (!roomDetails[matchId]?.room_id || !roomDetails[matchId]?.room_password) {
      toast.error('Please provide both Room ID and Password');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `https://backend.aubattle.com/api/admin/update-room/${matchId}`,
        roomDetails[matchId],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room details saved successfully!');
      fetchMatches();
    } catch (error) {
      toast.error('Failed to save room details');
      console.error('Room details error:', error);
    }
  };

  const completeMatch = async (matchId) => {
    if (!selectedWinner[matchId] || !resultImage[matchId]) {
      toast.error('Please select winner and upload result image');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('result_image', resultImage[matchId]);
      formData.append('winner_id', selectedWinner[matchId]);

      const token = localStorage.getItem('token');
      await axios.post(
        `https://backend.aubattle.com/api/admin/complete-match/${matchId}`,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}` 
          } 
        }
      );

      toast.success('Match completed successfully!');
      fetchMatches();
      setSelectedWinner(prev => ({ ...prev, [matchId]: null }));
      setResultImage(prev => ({ ...prev, [matchId]: null }));
    } catch (error) {
      toast.error('Failed to complete match');
      console.error('Complete match error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this match?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://backend.aubattle.com/api/admin/delete-match/${matchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Match deleted successfully!');
      fetchMatches();
    } catch (error) {
      toast.error('Failed to delete match');
      console.error('Delete error:', error);
    }
  };

  const exportMatchData = (match) => {
    const dataStr = JSON.stringify(match, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `match_${match.match_id}_data.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  return (
    <div className="admin-match-players">
      <div className="header">
        <h1><FaTrophy /> Match Management Dashboard</h1>
        <p>Manage tournaments, players, and match results</p>
      </div>

      {/* Controls Section */}
      <div className="controls-section">
        <div className="search-filter">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search matches by game, tag or player..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <FaFilter />
            <select 
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              <option value="all">All Matches</option>
              <option value="upcoming">Upcoming</option>
              <option value="started">Live Matches</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        
        <div className="sort-actions">
          <button className="btn-refresh" onClick={fetchMatches}>
            <FaSpinner className={loading ? 'spinning' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <h3>{matches.length}</h3>
          <p>Total Matches</p>
        </div>
        <div className="stat-card">
          <h3>{matches.filter(m => m.match_status === 'upcoming').length}</h3>
          <p>Upcoming</p>
        </div>
        <div className="stat-card">
          <h3>{matches.filter(m => m.match_status === 'started').length}</h3>
          <p>Live Now</p>
        </div>
        <div className="stat-card">
          <h3>{matches.filter(m => m.match_status === 'completed').length}</h3>
          <p>Completed</p>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading">
          <FaSpinner className="spinner" /> Loading matches...
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredMatches.length === 0 && (
        <div className="empty-state">
          No matches found matching your criteria
        </div>
      )}

      {/* Matches Table */}
      <div className="matches-table">
        <div className="table-header">
          <div className="header-cell" onClick={() => requestSort('game_name')}>
            Game <FaSort />
          </div>
          <div className="header-cell" onClick={() => requestSort('match_datetime')}>
            Date & Time <FaSort />
          </div>
          <div className="header-cell" onClick={() => requestSort('players')}>
            Players <FaSort />
          </div>
          <div className="header-cell" onClick={() => requestSort('prize_pool')}>
            Prize Pool <FaSort />
          </div>
          <div className="header-cell">
            Status
          </div>
          <div className="header-cell actions">
            Actions
          </div>
        </div>

        {filteredMatches.map(match => (
          <div key={match.match_id} className={`table-row ${match.match_status}`}>
            <div className="table-cell">
              <div className="game-info">
                {match.game_image && (
                  <img 
                    src={`https://backend.aubattle.com/public/uploads/${match.game_image}`} 
                    alt={match.game_name}
                    className="game-image"
                  />
                )}
                <div>
                  <strong>{match.game_name || 'Custom Match'}</strong>
                  {match.match_tag && <span className="match-tag">{match.match_tag}</span>}
                </div>
              </div>
            </div>
            <div className="table-cell">
              {formatDate(match.match_datetime)}
            </div>
            <div className="table-cell">
              {match.players.length}/{match.max_players || '∞'}
            </div>
            <div className="table-cell">
              ₹{match.prize_pool}
            </div>
            <div className="table-cell">
              <span className={`status-badge ${match.match_status}`}>
                {match.match_status.toUpperCase()}
              </span>
            </div>
            <div className="table-cell actions">
              <button 
                className="btn-icon view"
                onClick={() => setExpandedMatch(expandedMatch === match.match_id ? null : match.match_id)}
              >
                {expandedMatch === match.match_id ? 'Hide Details' : 'View Details'}
              </button>
              <button 
                className="btn-icon export"
                onClick={() => exportMatchData(match)}
                title="Export match data"
              >
                <FaDownload />
              </button>
              {match.match_status !== 'completed' && (
                <button 
                  className="btn-icon delete"
                  onClick={() => deleteMatch(match.match_id)}
                  title="Delete match"
                >
                  <FaTrash />
                </button>
              )}
            </div>

            {/* Expanded Match Details */}
            {expandedMatch === match.match_id && (
              <div className="expanded-details">
                <div className="details-section">
                  <h4>Match Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Match ID</label>
                      <span>#{match.match_id}</span>
                    </div>
                    <div className="info-item">
                      <label>Entry Fee</label>
                      <span>₹{match.entry_fee}</span>
                    </div>
                    <div className="info-item">
                      <label>Created At</label>
                      <span>{new Date(match.created_at).toLocaleString()}</span>
                    </div>
                    {match.room_id && (
                      <div className="info-item">
                        <label>Room Details</label>
                        <span>ID: {match.room_id} | Pass: {match.room_password}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h4>Participants ({match.players.length})</h4>
                  <div className="players-grid">
                    {match.players.map(player => (
                      <div key={player.id} className={`player-card ${match.winner_id === player.id ? 'winner' : ''}`}>
                        <div className="player-header">
                          <h5>{player.name}</h5>
                          {match.winner_id === player.id && (
                            <span className="winner-badge"><FaCrown /> Winner</span>
                          )}
                        </div>
                        <div className="player-details">
                          <div className="detail">
                            <label>Email</label>
                            <span>{player.email}</span>
                          </div>
                          <div className="detail">
                            <label>Slot</label>
                            <span>{player.slot_number || 'N/A'}</span>
                          </div>
                          <div className="detail">
                            <label>Coins Won</label>
                            <MatchPlayerCoinsEditor
                              player={player}
                              matchId={match.match_id}
                              refreshData={fetchMatches}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Sections */}
                {match.match_status === 'upcoming' && !match.room_id && (
                  <div className="action-section">
                    <h4>Add Room Details</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label><FaDoorOpen /> Room ID</label>
                        <input
                          type="text"
                          placeholder="Enter room ID"
                          value={roomDetails[match.match_id]?.room_id || ''}
                          onChange={(e) => setRoomDetails(prev => ({
                            ...prev,
                            [match.match_id]: {
                              ...prev[match.match_id],
                              room_id: e.target.value
                            }
                          }))}
                        />
                      </div>
                      <div className="form-group">
                        <label><FaKey /> Password</label>
                        <input
                          type="text"
                          placeholder="Enter room password"
                          value={roomDetails[match.match_id]?.room_password || ''}
                          onChange={(e) => setRoomDetails(prev => ({
                            ...prev,
                            [match.match_id]: {
                              ...prev[match.match_id],
                              room_password: e.target.value
                            }
                          }))}
                        />
                      </div>
                    </div>
                    <button
                      className="btn-primary"
                      onClick={() => submitRoomDetails(match.match_id)}
                    >
                      Save Room Details
                    </button>
                  </div>
                )}

                {match.match_status === 'started' && (
                  <div className="action-section">
                    <h4>Complete Match</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Select Winner</label>
                        <select
                          value={selectedWinner[match.match_id] || ''}
                          onChange={(e) => setSelectedWinner(prev => ({
                            ...prev,
                            [match.match_id]: e.target.value
                          }))}
                        >
                          <option value="">Select winner</option>
                          {match.players.map(player => (
                            <option key={player.id} value={player.id}>
                              {player.name} ({player.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label><FaImage /> Result Screenshot</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setResultImage(prev => ({
                            ...prev,
                            [match.match_id]: e.target.files[0]
                          }))}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => completeMatch(match.match_id)}
                      disabled={loading}
                    >
                      {loading ? <FaSpinner className="spinner" /> : 'Complete Match'}
                    </button>
                  </div>
                )}

                {match.match_status === 'completed' && match.result_image && (
                  <div className="action-section">
                    <h4>Match Result</h4>
                    <div className="result-display">
                      <img 
                        src={`https://backend.aubattle.com/public/uploads/${match.result_image}`} 
                        alt="Match result" 
                        className="result-image"
                      />
                      <div className="winner-info">
                        <h5>Winner: {match.players.find(p => p.id === match.winner_id)?.name}</h5>
                        <p>Completed at: {new Date(match.updated_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMatchPlayers;